import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { CredentialingV2 } from '~/constants/ff.posthog';
import { useCheckPaymentExpiration } from '~/components/Inbox/Notification/Reminders/hooks/useCheckPaymentExpiration';
import { useSelectedOffice } from '~/hooks/useSelectedOffice';
import type { ILicense, ProfessionTypeEnum } from '~/models';
import { CredentialV2Enum, CredentialV2TypeEnum } from '../types/Credentials';
import type { ICredentialNumberData } from '../types/Credentials';
import { useGetAuthorizationCredentials } from './useGetAuthorizationCredentials';

interface UseGetAuthorizationProfessionalParams {
  professionalId: string;
  candidateLicense?: ILicense;
  jobProfessionName?: string;
  offerOwnerId?: string;
  jobProfession?: ProfessionTypeEnum;
  jobSubProfession?: string | null;
  jobState?: string;
}

const getV1ProfessionName = (
  candidateLicense?: ILicense,
  jobProfessionName?: string,
  jobSubProfession?: string | null,
) =>
  UserSubProfession.getName(jobSubProfession || candidateLicense?.subProfession || '') ??
  jobProfessionName ??
  UserProfession.getName(candidateLicense?.profession ?? '') ??
  candidateLicense?.professionName;

const getV1LicenseNumber = (candidateLicense?: ILicense) =>
  candidateLicense?.credentials?.find(({ entry_field }) => entry_field?.license)?.entry_field
    ?.license;

export const useGetAuthorizationProfessional = ({
  professionalId,
  candidateLicense,
  jobProfessionName,
  offerOwnerId,
  jobProfession,
  jobSubProfession,
  jobState,
}: UseGetAuthorizationProfessionalParams) => {
  const { payments } = useCheckPaymentExpiration();
  const childOffice = useSelectedOffice(offerOwnerId);
  const hasValidPayment = childOffice?.is_pre_approved || payments?.length;
  const isCredentialV2 = useFeatureFlag(CredentialingV2);

  const { authorizations: _authorizations, isLoading } = useGetAuthorizationCredentials(
    professionalId,
    isCredentialV2,
  );

  const authorizations = jobProfession
    ? _authorizations?.filter(
        (a) =>
          (jobSubProfession ? a.profession === jobSubProfession : a.profession === jobProfession) &&
          a.state === jobState,
      ) ?? null
    : _authorizations;
  const authorization =
    authorizations?.find((a) => a.isAuthorized) || authorizations?.[0];

  const v1ProfessionName = getV1ProfessionName(
    candidateLicense,
    jobProfessionName,
    jobSubProfession,
  );
  const v1LicenseNumber = getV1LicenseNumber(candidateLicense);

  const v2ProfessionName = authorization
    ? UserProfession.getName(authorization.profession)
    : undefined;

  const v2LicenseData = authorization?.credentials?.find(
    (c) =>
      c.credential === CredentialV2Enum.StateLicense && c.type === CredentialV2TypeEnum.Number,
  )?.data as ICredentialNumberData | undefined;

  const v1Profession = candidateLicense?.subProfession ?? candidateLicense?.profession;
  const v2Profession = authorization?.profession;

  const displayProfession = isCredentialV2 ? (v2Profession ?? v1Profession) : v1Profession;

  const displayProfessionName = isCredentialV2
    ? (v2ProfessionName ?? v1ProfessionName)
    : v1ProfessionName;

  const displayLicenseNumber = isCredentialV2
    ? (v2LicenseData?.number?.credentialNumber ?? v1LicenseNumber)
    : v1LicenseNumber;

  return {
    displayProfession,
    displayProfessionName,
    displayLicenseNumber: hasValidPayment ? displayLicenseNumber : '******',
    isCredentialV2,
    isLoading,
    authorizations: authorizations ?? [],
  };
};
